import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { createChatManager } from '../chatManager';
import { buildHiddenContextBlock } from '../utils/chatHelpers';
import { breezeEngineRegistry } from '../../sdk/BreezeEngineRegistry';
function buildContextMessage(prompt, registry) {
  const mergedContext = Object.assign({}, registry.getContext(), prompt.context);
  if (Object.keys(mergedContext).length === 0) return prompt.message;
  return `${buildHiddenContextBlock(mergedContext)}${prompt.message}`;
}
function buildSendOptions(prompt) {
  const {
    turnMetadata,
    messageContexts,
    conversationMetadata,
    fileReferences,
    collectionId,
    hidden
  } = prompt;
  if (!turnMetadata && !(messageContexts !== null && messageContexts !== void 0 && messageContexts.length) && !conversationMetadata && !(fileReferences !== null && fileReferences !== void 0 && fileReferences.length) && !collectionId && !hidden) return undefined;
  return Object.assign({}, turnMetadata && {
    turnMetadata
  }, (messageContexts === null || messageContexts === void 0 ? void 0 : messageContexts.length) && {
    messageContexts
  }, conversationMetadata && {
    conversationMetadata
  }, (fileReferences === null || fileReferences === void 0 ? void 0 : fileReferences.length) && {
    fileReferences
  }, collectionId && {
    collectionId
  }, hidden && {
    hidden
  });
}
export function useChatManager(options) {
  var _registry, _snapshot$active2;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const managerRef = useRef(null);
  if (managerRef.current === null) {
    managerRef.current = createChatManager(Object.assign({}, options, {
      onError: (cId, error) => {
        var _optionsRef$current$o, _optionsRef$current;
        return (_optionsRef$current$o = (_optionsRef$current = optionsRef.current).onError) === null || _optionsRef$current$o === void 0 ? void 0 : _optionsRef$current$o.call(_optionsRef$current, cId, error);
      },
      onConversationCreated: cId => {
        var _optionsRef$current$o2, _optionsRef$current2;
        return (_optionsRef$current$o2 = (_optionsRef$current2 = optionsRef.current).onConversationCreated) === null || _optionsRef$current$o2 === void 0 ? void 0 : _optionsRef$current$o2.call(_optionsRef$current2, cId);
      },
      onStatusChange: (cId, status, prevStatus, activeTools) => {
        var _optionsRef$current$o3, _optionsRef$current3;
        return (_optionsRef$current$o3 = (_optionsRef$current3 = optionsRef.current).onStatusChange) === null || _optionsRef$current$o3 === void 0 ? void 0 : _optionsRef$current$o3.call(_optionsRef$current3, cId, status, prevStatus, activeTools);
      },
      onStreamEvent: (cId, event) => {
        var _optionsRef$current$o4, _optionsRef$current4;
        return (_optionsRef$current$o4 = (_optionsRef$current4 = optionsRef.current).onStreamEvent) === null || _optionsRef$current$o4 === void 0 ? void 0 : _optionsRef$current$o4.call(_optionsRef$current4, cId, event);
      },
      onTitleChanged: (cId, title) => {
        var _optionsRef$current$o5, _optionsRef$current5;
        return (_optionsRef$current$o5 = (_optionsRef$current5 = optionsRef.current).onTitleChanged) === null || _optionsRef$current$o5 === void 0 ? void 0 : _optionsRef$current$o5.call(_optionsRef$current5, cId, title);
      }
    }));
  }
  const manager = managerRef.current;
  const snapshot = useSyncExternalStore(useCallback(cb => manager.subscribe(cb), [manager]), useCallback(() => manager.getSnapshot(), [manager]));
  const prevAgentIdRef = useRef(options.agentId);
  if (options.agentId !== prevAgentIdRef.current) {
    prevAgentIdRef.current = options.agentId;
    manager.setAgentId(options.agentId);
  }
  const ablyAuthToken = options.mode === 'ably' ? options.getAblyAuthToken : undefined;
  const prevAblyAuthTokenRef = useRef(ablyAuthToken);
  if (ablyAuthToken !== prevAblyAuthTokenRef.current) {
    prevAblyAuthTokenRef.current = ablyAuthToken;
    manager.setAblyAuthToken(ablyAuthToken);
  }
  const registryRef = useRef((_registry = options.__registry) !== null && _registry !== void 0 ? _registry : breezeEngineRegistry);
  const pendingPromptRef = useRef(null);
  useEffect(() => {
    const registry = registryRef.current;
    return registry.addListener(() => {
      var _manager$getSnapshot$;
      // Handle openConversation() requests — open the specified conversation
      // without sending a message. Handles the "panel already open" case where
      // BreezeEngineThreadsInit has already run and won't re-execute.
      const requestedConversationId = registry.getRequestedConversationId();
      if (requestedConversationId !== null) {
        manager.open(requestedConversationId);
        registry.clearRequestedConversationId();
        return;
      }
      const prompt = registry.getQueuedPrompt();
      if (!(prompt !== null && prompt !== void 0 && prompt.message)) return;
      if (prompt.agentId && prompt.agentId !== optionsRef.current.agentId) {
        pendingPromptRef.current = prompt;
        return;
      }
      if (prompt.startNewConversation) {
        manager.startNew();
      }
      if (((_manager$getSnapshot$ = manager.getSnapshot().active) === null || _manager$getSnapshot$ === void 0 ? void 0 : _manager$getSnapshot$.status) === 'READY') {
        void manager.sendMessage(prompt.hidden ? prompt.message : buildContextMessage(prompt, registryRef.current), buildSendOptions(prompt));
        registry.clearQueuedPrompt();
      } else {
        pendingPromptRef.current = prompt;
      }
    });
  }, [manager]);
  useEffect(() => {
    var _manager$getSnapshot$2;
    const prompt = pendingPromptRef.current;
    if (!(prompt !== null && prompt !== void 0 && prompt.agentId) || prompt.agentId !== options.agentId) return;
    pendingPromptRef.current = null;
    if (prompt.startNewConversation) {
      manager.startNew();
    }
    if (((_manager$getSnapshot$2 = manager.getSnapshot().active) === null || _manager$getSnapshot$2 === void 0 ? void 0 : _manager$getSnapshot$2.status) === 'READY') {
      void manager.sendMessage(prompt.hidden ? prompt.message : buildContextMessage(prompt, registryRef.current), buildSendOptions(prompt));
      registryRef.current.clearQueuedPrompt();
    }
  }, [manager, options.agentId]);
  const prevActiveStatusRef = useRef(undefined);
  useEffect(() => {
    var _snapshot$active;
    const currentStatus = (_snapshot$active = snapshot.active) === null || _snapshot$active === void 0 ? void 0 : _snapshot$active.status;
    const prevStatus = prevActiveStatusRef.current;
    prevActiveStatusRef.current = currentStatus;
    const prompt = pendingPromptRef.current;
    if (currentStatus === 'READY' && prevStatus !== 'READY' && prompt && (!prompt.agentId || prompt.agentId === options.agentId)) {
      pendingPromptRef.current = null;
      void manager.sendMessage(prompt.hidden ? prompt.message : buildContextMessage(prompt, registryRef.current), buildSendOptions(prompt));
      registryRef.current.clearQueuedPrompt();
    }
  }, [manager, options.agentId, (_snapshot$active2 = snapshot.active) === null || _snapshot$active2 === void 0 ? void 0 : _snapshot$active2.status]);
  const prevToolsRef = useRef(options.tools);
  if (options.tools !== prevToolsRef.current) {
    prevToolsRef.current = options.tools;
    if (options.tools) {
      manager.setTools(options.tools);
    }
  }
  const skillSpecsOption = 'externalSkillSpecs' in options ? options.externalSkillSpecs : undefined;
  const prevSkillSpecsRef = useRef(skillSpecsOption);
  if (skillSpecsOption !== prevSkillSpecsRef.current) {
    prevSkillSpecsRef.current = skillSpecsOption;
    manager.setExternalSkillSpecs(skillSpecsOption !== null && skillSpecsOption !== void 0 ? skillSpecsOption : []);
  }
  const subagentSpecsOption = 'externalSubagentSpecs' in options ? options.externalSubagentSpecs : undefined;
  const prevSubagentSpecsRef = useRef(subagentSpecsOption);
  if (subagentSpecsOption !== prevSubagentSpecsRef.current) {
    prevSubagentSpecsRef.current = subagentSpecsOption;
    manager.setExternalSubagentSpecs(subagentSpecsOption !== null && subagentSpecsOption !== void 0 ? subagentSpecsOption : []);
  }
  useEffect(() => {
    return () => {
      manager.destroy();
      managerRef.current = null;
    };
  }, [manager]);
  return useMemo(() => ({
    conversations: snapshot.conversations,
    activeConversationId: snapshot.activeConversationId,
    active: snapshot.active,
    open: manager.open,
    startNew: manager.startNew,
    close: manager.close,
    setActive: manager.setActive,
    sendMessage: manager.sendMessage,
    stop: manager.stop,
    clear: manager.clear,
    submitFeedback: manager.submitFeedback,
    addToolResult: manager.addToolResult,
    approveTool: manager.approveTool,
    removeQueuedMessage: manager.removeQueuedMessage,
    resumeTurn: manager.resumeTurn,
    loadMore: manager.loadMore,
    setTools: manager.setTools,
    setExternalSkillSpecs: manager.setExternalSkillSpecs,
    setExternalSubagentSpecs: manager.setExternalSubagentSpecs,
    injectMessage: manager.injectMessage
  }), [manager, snapshot]);
}